import React from 'react';
import { C } from '@parsina/framework/clinical/src/constants/theme';
import { SearchInput, Select, Button, BottomSheet, JalaliDatePicker } from '@parsina/framework';
import { CalendarDays, SlidersHorizontal, FilterX } from 'lucide-react';
import { useTranslation } from '@parsina/framework/clinical/src/i18n/index';

// ─────────────────────────────────────────────────────────────────────────────
// The mobile filter bar (DESIGN-CONCEPT-MOBILE v2 §6) — the shape every filtered
// list on the phone wears.
//
// The rule it encodes: THE TWO MOST FREQUENT CONTROLS STAY INLINE, everything
// else goes in a sheet. On حسابداری that is search + settlement; on سوابق
// ویزیت‌ها it is search + status. Anything more on a 320px bar either wraps into
// three rows or shrinks below a thumb.
//
// Generalised out of `MobileAccountingFilters` when the encounters screen needed
// the same bar (§E.2). The two screens differ in exactly three things, so those
// are props: which chips are inline (`chips`), what the extra sheet contains
// (`extraFilters`), and the testid prefix each screen's selector contract
// already names.
// ─────────────────────────────────────────────────────────────────────────────

const MobileFilterBar = ({
  testId,
  search, onSearch, searchPlaceholder,
  chips = [], chipValue, onChip, chipsLabel,
  presets = [], preset, custom = {}, onPreset, onCustom, rangeLabel,
  extraFilters,
  activeCount = 0, onClear, clearLabel,
}) => {
  const { t, formatDigits } = useTranslation();
  const [rangeOpen, setRangeOpen] = React.useState(false);
  const [moreOpen, setMoreOpen] = React.useState(false);

  // Custom-range draft — committed only when both ends are set, so a half-picked
  // range never collapses the list to «امروز» mid-edit.
  const [draft, setDraft] = React.useState({ from: custom.from || '', to: custom.to || '' });
  React.useEffect(() => {
    setDraft({ from: custom.from || '', to: custom.to || '' });
  }, [custom.from, custom.to]);

  const clearText = clearLabel || t('common.actions.clearFilters');

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.625rem' }} data-testid={testId}>

      {/* Search — leads the bar, full width. */}
      <div data-testid={`${testId}-search`}>
        <SearchInput
          value={search}
          onChange={(e) => onSearch(e.target.value)}
          placeholder={searchPlaceholder}
          clearLabel={t('common.actions.clearSearch')}
        />
      </div>

      {/* The inline group — a real 2.75rem touch target row, not the kit's
          segmented pill, which is too short for a thumb (§6). */}
      {chips.length > 0 && (
        <div
          role="tablist" aria-label={chipsLabel}
          data-testid={`${testId}-chips`}
          style={{ display: 'flex', gap: '0.375rem' }}
        >
          {chips.map((item) => {
            const active = chipValue === item.id;
            return (
              <Button
                key={item.id}
                role="tab" aria-selected={active}
                variant={active ? 'default' : 'outline'}
                size="sm"
                className="flex-1 min-h-11 px-2"
                data-testid={`${testId}-chips-${item.id}`}
                onClick={() => onChip(item.id)}
              >
                {item.label}
              </Button>
            );
          })}
        </div>
      )}

      {/* Range + more-filters — two labelled buttons opening their sheets. */}
      <div style={{ display: 'flex', gap: '0.5rem' }}>
        {presets.length > 0 && (
          <Button
            variant="outline" size="sm"
            onClick={() => setRangeOpen(true)}
            data-testid={`${testId}-range`}
            className="flex-1 min-h-11 justify-start gap-1.5"
          >
            <CalendarDays size={15} aria-hidden />
            <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{rangeLabel}</span>
          </Button>
        )}

        {extraFilters && (
          <Button
            variant="outline" size="sm"
            onClick={() => setMoreOpen(true)}
            data-testid={`${testId}-more`}
            className="flex-1 min-h-11 justify-start gap-1.5"
          >
            <SlidersHorizontal size={15} aria-hidden />
            {t('common.actions.filters')}
            {activeCount > 0 && (
              <span style={{
                marginInlineStart: 'auto',
                minWidth: '1.25rem', height: '1.25rem', borderRadius: '0.75rem',
                background: C.navy, color: 'white',
                fontSize: '0.6875rem', fontWeight: 800,
                display: 'inline-flex', alignItems: 'center', justifyContent: 'center', padding: '0 0.375rem',
              }}>
                {formatDigits(activeCount)}
              </span>
            )}
          </Button>
        )}
      </div>

      {activeCount > 0 && onClear && (
        <Button
          variant="ghost" size="sm"
          onClick={onClear}
          data-testid={`${testId}-clear`}
          className="self-start min-h-11 gap-1.5 text-muted-foreground"
        >
          <FilterX size={15} aria-hidden />
          {clearText}
        </Button>
      )}

      {/* ── Range sheet ── */}
      {presets.length > 0 && (
        <BottomSheet
          open={rangeOpen}
          onClose={() => setRangeOpen(false)}
          title={t('common.labels.dateRange')}
          closeLabel={t('common.actions.close')}
          data-testid={`${testId}-range-sheet`}
        >
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }} data-testid={`${testId}-range-presets`}>
            {presets.map((p) => (
              <Button
                key={p.id}
                variant={preset === p.id ? 'default' : 'outline'}
                size="sm"
                className="min-h-11"
                onClick={() => { onPreset(p.id); setRangeOpen(false); }}
              >
                {p.label}
              </Button>
            ))}
          </div>

          <div style={{ marginTop: '1rem', paddingTop: '1rem', borderTop: `1px solid ${C.border}` }}>
            <div style={{ fontSize: '0.75rem', fontWeight: 700, color: C.textMuted, marginBottom: '0.5rem' }}>
              {t('common.labels.customRange')}
            </div>
            <div style={{ display: 'flex', gap: '0.5rem' }}>
              <div style={{ flex: 1 }}>
                <JalaliDatePicker value={draft.from} onChange={(v) => setDraft((d) => ({ ...d, from: v }))} placeholder={t('common.labels.fromDate')} disableFuture />
              </div>
              <div style={{ flex: 1 }}>
                <JalaliDatePicker value={draft.to} onChange={(v) => setDraft((d) => ({ ...d, to: v }))} placeholder={t('common.labels.toDate')} disableFuture />
              </div>
            </div>
            <Button
              variant="default"
              className="mt-3 w-full min-h-11"
              disabled={!draft.from || !draft.to}
              onClick={() => { onCustom(draft.from, draft.to); setRangeOpen(false); }}
            >
              {t('accounting.applyCustomRange')}
            </Button>
          </div>
        </BottomSheet>
      )}

      {/* ── More-filters sheet ── */}
      {extraFilters && (
        <BottomSheet
          open={moreOpen}
          onClose={() => setMoreOpen(false)}
          title={t('common.actions.filters')}
          closeLabel={t('common.actions.close')}
          data-testid={`${testId}-more-sheet`}
        >
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.875rem' }}>
            {extraFilters}

            <Button variant="default" className="w-full min-h-11" onClick={() => setMoreOpen(false)}>
              {t('accounting.viewResults')}
            </Button>
            {activeCount > 0 && onClear && (
              <Button variant="ghost" className="w-full min-h-11 gap-1.5 text-muted-foreground" onClick={() => { onClear(); setMoreOpen(false); }}>
                <FilterX size={15} aria-hidden />
                {clearText}
              </Button>
            )}
          </div>
        </BottomSheet>
      )}
    </div>
  );
};

/** A labelled control for the «فیلترها» sheet — one shape for every screen's. */
export const FilterField = ({ label, children, testId }) => (
  <label style={{ display: 'block' }} data-testid={testId}>
    <span style={{ fontSize: '0.75rem', fontWeight: 700, color: C.textMuted, display: 'block', marginBottom: '0.375rem' }}>
      {label}
    </span>
    {children}
  </label>
);

export { Select };
export default MobileFilterBar;
